import React, { type SyntheticEvent, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import Tooltip from '@atlaskit/tooltip';
import { useIsCurrentBrowserSupported } from '@atlassian/jira-insights-common/src/controllers/use-is-current-browser-supported/index.tsx';
import { useHasPermission } from '@atlassian/jira-insights-common/src/services/permissions/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import ResponsiveButton from '../../common/ui/responsive-button';
import { useSidebarStore } from '../../controllers/sidebar/main.tsx';
import { SidebarStatuses } from '../../controllers/sidebar/types.tsx';
import { useProject } from '../../services/get-project';
import messages from './messages';

const VIEW_AGGREGATED_DATA_PERMISSION = 'VIEW_AGGREGATED_DATA';

const isButtonDisabled = (
	project: { isSimplified: boolean } | undefined,
	hasPermission: boolean,
) => {
	if (project === undefined) {
		return true;
	}

	if (project.isSimplified) {
		return false;
	}

	return !hasPermission;
};

const InsightsButton = () => {
	const isSupported = useIsCurrentBrowserSupported();

	const { formatMessage } = useIntl();
	const [sidebarState, sidebarActions] = useSidebarStore();
	const isShowingInsights = sidebarState.status === SidebarStatuses.SHOWING_INSIGHTS;
	const { data: project, loading: projectLoading } = useProject();
	const { hasPermission, isLoading: permissionLoading } = useHasPermission(
		VIEW_AGGREGATED_DATA_PERMISSION,
	);
	const isDisabled = isButtonDisabled(project, hasPermission);
	const isSiteAdmin = useIsSiteAdmin();
	const isAdmin = useIsAdmin();
	const isProjectAdmin = !!project?.isProjectAdmin;
	const fireAnalytics = !permissionLoading && !projectLoading;

	const onClick = (
		event: SyntheticEvent<HTMLElement> | MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (isShowingInsights) {
			sidebarActions.close();
			return;
		}
		fireUIAnalytics(analyticsEvent, 'viewInsights', {
			isDisabled,
		});
		sidebarActions.showInsights();
	};

	const isKindOfAdmin = !!(isSiteAdmin || isAdmin || isProjectAdmin);

	const disabledTooltipText = isKindOfAdmin
		? formatMessage(messages.buttonDisabledForAdmins)
		: formatMessage(messages.buttonDisabledForNonAdmins);

	const enabledTooltipText = formatMessage(messages.tooltip);

	const tooltipContent = isDisabled ? disabledTooltipText : enabledTooltipText;

	if (!isSupported) {
		return null;
	}

	return (
		<>
			{fireAnalytics && (
				<FireUiAnalytics
					action="rendered"
					actionSubject="button"
					actionSubjectId="toggleInsightsPanel"
					attributes={{ disabled: isDisabled }}
				/>
			)}
			<Tooltip position="bottom" content={tooltipContent}>
				<ResponsiveButton
					isDisabled={isDisabled}
					onClick={onClick}
					isSelected={isShowingInsights}
					text={formatMessage(messages.insights)}
					icon={<GraphLineIcon label="" />}
					// Note: This test ID is used in Pollinator checks
					testId="software-deployments.ui.insights-button"
				/>
			</Tooltip>
		</>
	);
};

export default InsightsButton;
